import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { DashHeaderIndex } from "@Src/features/Dashboard/Dash-header/Dash-header";
import { DashboardSidebarIndex } from "@Src/features/Dashboard/Sidebar/Index";
import { Paginator } from "primereact/paginator";
import { getHomeCmsList, getVideoList } from "@Src/features/Home/services/Home.service";
import { VideoData, VideoListResponse } from "@Src/features/Home/services/Home.api.interface";
import { useAppSelector } from "@Src/app/hooks";
import { VideoCard } from "@Src/components/VideoCard";
import { VideoListingFilter } from "./components/VideoListingFilter/VideoListingFilter";
import { FILTER_KEYS } from "@Src/features/Filter/Constants/filters.constants";
import { getCombinedClassList } from "@Src/features/Auth/components/exam_prefrences/services/examPrefrences.service";
import { APIResponse, APIStatus } from "@Src/services/baseInterfaces";
import "./VideoListing.scss";

export const VideoListing = () => {
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<any>({});
  const [initialFilters, setInitialFilters] = useState<Array<any>>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [cmsData, setCmsData] = useState<any[]>([]);
  const [classOptions, setClassOptions] = useState<any[]>([]);
  const initializedFiltersRef = useRef<string | null>(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const ITEMS_PER_PAGE = 8;

  const coursePreferences = useAppSelector((state: any) => state.coursePreferences);
  const isLoggedIn = useAppSelector((state: any) => state.login.isLoggedIn);
  const location = useLocation();

  // Resolve the combined class options before making the first video request.
  useEffect(() => {
    const board = coursePreferences?.boardDetails?.[0];
    const classDetail = coursePreferences?.classDetails?.[0];
    const routeFilters = location.state || {};
    const classId = routeFilters.classId || classDetail?.Id;
    if (!classId) {
      return;
    }

    const initializationKey = JSON.stringify({
      classId,
      boardId: routeFilters.boardId || board?.Id,
      routeFilters,
      isLoggedIn,
    });
    if (initializedFiltersRef.current === initializationKey) {
      return;
    }
    initializedFiltersRef.current = initializationKey;

    const initializeFilters = async () => {
      let resolvedClassOptions = coursePreferences?.classDetails || [];
      try {
        const response: APIResponse = await getCombinedClassList({ classId: classDetail?.Id || classId });
        if (response.ResponseType === APIStatus.SUCCESS && Array.isArray(response.data)) {
          resolvedClassOptions = response.data.map((item: any) => ({
            Id: Number(item?.id ?? item?.Id),
            Name: item?.name ?? item?.Name,
          }));
        }
      } catch (error) {
        console.error("Error fetching combined class list:", error);
      }

      setClassOptions(resolvedClassOptions);

      const finalBoard = routeFilters.boardId
        ? { Id: routeFilters.boardId, Name: routeFilters.boardName }
        : board;
      const finalClass = routeFilters.classId
        ? { Id: routeFilters.classId, Name: routeFilters.className }
        : resolvedClassOptions[0] || classDetail;
      const finalSubject = routeFilters.subjectId
        ? [{ Id: routeFilters.subjectId, Name: routeFilters.subjectName }]
        : routeFilters.subjectList?.length > 0 ? routeFilters.subjectList : [];
      const finalChapter = routeFilters.chapterId
        ? [{ Id: routeFilters.chapterId, Name: routeFilters.chapterName }]
        : routeFilters.chapterList?.length > 0 ? routeFilters.chapterList : [];
      const finalTopic = routeFilters.topicId
        ? [{ Id: routeFilters.topicId, Name: routeFilters.topicName }]
        : routeFilters.topicList?.length > 0 ? routeFilters.topicList : [];

      const initialFiltersArr: any[] = [];
      if (finalBoard?.Id) initialFiltersArr.push({ [FILTER_KEYS.BOARD]: finalBoard });
      if (finalClass?.Id) initialFiltersArr.push({ [FILTER_KEYS.CLASS]: finalClass });
      if (finalSubject.length > 0) initialFiltersArr.push({ [FILTER_KEYS.SUBJECT]: finalSubject });
      if (finalChapter.length > 0) initialFiltersArr.push({ [FILTER_KEYS.CHAPTER]: finalChapter });
      if (finalTopic.length > 0) initialFiltersArr.push({ [FILTER_KEYS.TOPIC]: finalTopic });

      setInitialFilters(initialFiltersArr);
      submitFilters({
        [FILTER_KEYS.BOARD]: finalBoard || null,
        [FILTER_KEYS.CLASS]: finalClass || null,
        [FILTER_KEYS.SUBJECT]: finalSubject,
        [FILTER_KEYS.CHAPTER]: finalChapter,
        [FILTER_KEYS.TOPIC]: finalTopic,
      });

      if (location.state) {
        window.history.replaceState({}, document.title);
      }
    };

    initializeFilters();
  }, [coursePreferences?.boardDetails?.[0]?.Id, coursePreferences?.classDetails?.[0]?.Id, isLoggedIn, location.state]);

  const submitFilters = (filters: any) => {
    setCurrentFilters(filters);
    setCurrentPage(0);
    fetchVideos(filters, 1);
  };

  const fetchVideos = async (filters: any, pageNumber = 1) => {
    try {
      setLoading(true);
      setError(null);

      const boardId = filters[FILTER_KEYS.BOARD]?.Id;
      const classId = filters[FILTER_KEYS.CLASS]?.Id;
      const languageId = filters[FILTER_KEYS.LANGUAGE]?.Id;

      // Handle subjects - if manually selected, use those; otherwise fetch from API
      let subjectIds: number[] = [];
      const selectedSubject = filters[FILTER_KEYS.SUBJECT];
      if (Array.isArray(selectedSubject) && selectedSubject.length > 0) {
        subjectIds = selectedSubject.map((subject: any) => subject.Id);
      } else if (selectedSubject?.Id) {
        subjectIds = [selectedSubject.Id];
      } else if (boardId && classId) {
        const subjectResponse = await import('@Src/features/UserProfile/Services/UserProfile.sevice').then(m => m.getSubjectList({ boardId, classId }));
        if (subjectResponse.ResponseType === 'SUCCESS' && subjectResponse.data) {
          subjectIds = subjectResponse.data.map((s: any) => s.Id);
        }
      }

      // Handle chapters
      let chapterIds: number[] = [];
      if (filters[FILTER_KEYS.CHAPTER]?.length > 0) {
        chapterIds = filters[FILTER_KEYS.CHAPTER].map((c: any) => c.Id);
      }

      // Handle topics
      let topicIds: number[] = [];
      if (filters[FILTER_KEYS.TOPIC]?.length > 0) {
        topicIds = filters[FILTER_KEYS.TOPIC].map((t: any) => t.Id);
      }

      const filterData = {
        page: pageNumber,
        limit: ITEMS_PER_PAGE,
        ...(boardId && { boardId }),
        ...(classId && { classId }),
        ...(languageId && { languageId }),
        ...(subjectIds.length > 0 && { subjectId: subjectIds }),
        ...(chapterIds.length > 0 && { chapterId: chapterIds }),
        ...(topicIds.length > 0 && { topicId: topicIds })
      };

      const response: VideoListResponse = await getVideoList(filterData, isLoggedIn);

      if (response.success && response.data?.result_data) {
        setTotalRecords(response.data.total_record || 0);
        setVideos(response.data.result_data);
      } else {
        setVideos([]);
      }
    } catch (err) {
      console.error("Error fetching videos:", err);
      setError("Failed to load videos. Please try again later.");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (event: any) => {
    setCurrentPage(event.page);
    fetchVideos(currentFilters, event.page + 1);
  };

  const getHomeCmsListDetails = async () => {
    try {
      const response = await getHomeCmsList();
      if (response.ResponseType === "SUCCESS" && response.data) {
        setCmsData(response.data);

      }
    } catch (error) {
      console.error("Error fetching CMS data for Video Tutorials:", error);
    }
  }
  useEffect(() => {
    getHomeCmsListDetails();
  }, []);
  return (
    <>
      <div>
        <DashHeaderIndex toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div className="dashboard-container">
          <div className={`dashboard-sidebar ${isSidebarOpen ? 'show' : ''}`}>
            <DashboardSidebarIndex closeSidebar={() => setIsSidebarOpen(false)} />
          </div>
          <div className="dashboard-content-page" onClick={() => isSidebarOpen && setIsSidebarOpen(false)}>
            <div className="videotutorials-container">
              <div className="ia-container my-2 lg:my-6">
                <div className="tw-text-center mb-4">
                  <span dangerouslySetInnerHTML={{ __html: cmsData.find(item => item.KeyName === "video-title")?.Description || "<h2 >Grasp Chapters in <span>Minutes with Video Lessons</span></h2><p>Watch quick videos that explain the exact concepts you need across PCM, Biology & more. </p>" }}></span>
                </div>

                <VideoListingFilter
                  submitFilters={submitFilters}
                  initialFilters={initialFilters}
                  classOptions={classOptions}
                />

                <div className="videotutorials-items">
                  {loading ? (
                    <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
                      <p className="tw-text-gray-600">Loading videos...</p>
                    </div>
                  ) : error ? (
                    <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
                      <p className="tw-text-red-600">{error}</p>
                    </div>
                  ) : videos.length === 0 ? (
                    <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
                      <p className="tw-text-gray-600">Coming soon...</p>
                    </div>
                  ) : (
                    <div className="videotutorials-grid tw-mt-4">
                      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6">
                        {videos.map((video) => (
                          <div key={video.VideoId} className="videotutorials-item">
                            <VideoCard
                              videoId={video.VideoId}
                              videoTitle={video.VideoTitle}
                              description={video.Description}
                              videoLink={video.TrailerVideoLink || video.VideoLink || ''}
                              fullVideoLink={video.VideoLink}
                              video={video}
                              height={450}
                              maxWidth={450}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="tw-mt-8 tw-flex tw-justify-center">
                        <Paginator
                          first={currentPage * ITEMS_PER_PAGE}
                          rows={ITEMS_PER_PAGE}
                          totalRecords={totalRecords}
                          onPageChange={onPageChange}
                          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isSidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </>
  );
};
